import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { coachAPI, adaptiveAPI } from '../../services/api';
import CoachLayout from './CoachLayout';

/* ── Coverage bar colour thresholds ───────────────────────────────────────── */
function coverageColor(count) {
  if (count >= 25) return '#22c55e';
  if (count >= 10) return '#f59e0b';
  return '#ef4444';
}

/* ── Generate Items Modal ─────────────────────────────────────────────────── */
// Navigates to Content Studio with a pre-filled prompt instead of directly
// creating placeholder stubs. Coach reviews and approves each AI-generated
// item via the StructuredArtifactCard approval workflow before it is saved.
function GenerateModal({ domain, skillId, onClose }) {
  const navigate = useNavigate();
  const [count, setCount] = useState(5);
  const [difficulty, setDifficulty] = useState('medium');

  const handleNavigate = () => {
    if (!skillId) return;
    const skillLabel = skillId.split('.').pop().replace(/_/g, ' ');
    const domainLabel = domain.replace(/_/g, ' ');
    const prompt = encodeURIComponent(
      `Generate ${count} ${difficulty}-difficulty MCQ quiz items for the skill "${skillLabel}" ` +
      `in the "${domainLabel}" domain. ` +
      `Each item must have 4 answer choices (one correct), ` +
      `a clear explanation of why the correct answer is right and why the distractors are wrong, ` +
      `and an IRT difficulty level of "${difficulty}". ` +
      `Include variety across conceptual, applied, and scenario-based question styles.`
    );
    navigate(`/coach/content-studio?domain=${encodeURIComponent(domain)}&artifact_type=cat_item&prompt=${prompt}`);
    onClose();
  };

  return (
    <div className="coach-modal-overlay" onClick={onClose}>
      <div className="coach-modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <h3 className="coach-modal-title">Generate Items via Content Studio</h3>
        <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
          Configure the generation request for skill <strong>{skillId}</strong>.
          You'll review and <strong>approve</strong> each AI-generated item in Content Studio before it is saved.
        </p>
        <div className="coach-form-group">
          <label className="coach-label">How many items?</label>
          <input
            className="coach-input"
            type="number"
            min="1"
            max="20"
            value={count}
            onChange={(e) => setCount(e.target.value)}
          />
        </div>
        <div className="coach-form-group">
          <label className="coach-label">Difficulty</label>
          <select
            className="coach-select"
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
          >
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>
        <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#a78bfa' }}>
          ✨ You'll be taken to Content Studio where you can review each generated item and approve it before saving.
        </div>
        <div className="coach-modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            data-testid="generate-items-btn"
            onClick={handleNavigate}
          >
            Open Content Studio →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Inline Edit Form (rendered below a row) ─────────────────────────────── */
function InlineEditForm({ domain, item, onSave, onCancel }) {
  const [form, setForm] = useState({
    question_text: item.question_text || '',
    difficulty_label: item.difficulty_label || 'medium',
    b_param: item.b_param ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await adaptiveAPI.updateItemBankEntry(domain, item.item_id || item._id, {
        question_text: form.question_text,
        difficulty_label: form.difficulty_label,
        b_param: form.b_param !== '' ? Number(form.b_param) : undefined,
      });
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td colSpan={4} style={{ padding: '12px 16px', background: 'rgba(99,102,241,0.06)', borderTop: '1px solid rgba(99,102,241,0.2)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'end' }}>
          <div className="coach-form-group" style={{ margin: 0 }}>
            <label className="coach-label" style={{ fontSize: '0.75rem' }}>Question text</label>
            <textarea
              className="coach-textarea"
              value={form.question_text}
              onChange={set('question_text')}
              rows={3}
              style={{ fontSize: '0.82rem' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="coach-form-group" style={{ margin: 0 }}>
              <label className="coach-label" style={{ fontSize: '0.75rem' }}>Difficulty</label>
              <select className="coach-select" value={form.difficulty_label} onChange={set('difficulty_label')} style={{ fontSize: '0.82rem', width: 110 }}>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            <div className="coach-form-group" style={{ margin: 0 }}>
              <label className="coach-label" style={{ fontSize: '0.75rem' }}>IRT b-param</label>
              <input
                className="coach-input"
                type="number"
                step="0.1"
                value={form.b_param}
                onChange={set('b_param')}
                style={{ fontSize: '0.82rem', width: 110 }}
                placeholder="e.g. 0.5"
              />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? '…' : 'Save'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={onCancel}>Cancel</button>
          </div>
        </div>
        {error && <div className="coach-error" style={{ marginTop: 8, fontSize: '0.8rem' }}>⚠ {error}</div>}
      </td>
    </tr>
  );
}

/* ── Main Page ───────────────────────────────────────────────────────────── */
export default function ItemBankManagerPage() {
  const { domain: urlDomain } = useParams();
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(urlDomain || '');
  const [skills, setSkills] = useState([]);
  const [activeSkillId, setActiveSkillId] = useState(null);
  const [items, setItems] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const PER_PAGE = 20;

  // Load domains; pre-select URL domain if present, else first returned
  useEffect(() => {
    coachAPI.listMyDomains()
      .then(({ data }) => {
        const raw = data?.domains;
        let list = [];
        if (Array.isArray(raw)) {
          list = raw.map((d) => (typeof d === 'string' ? d : d?.domain)).filter(Boolean);
        } else if (raw && typeof raw === 'object') {
          list = Object.keys(raw);
        }
        if (list.length) {
          setDomains(list);
          setSelectedDomain((prev) => {
            if (prev && list.includes(prev)) return prev;
            return urlDomain && list.includes(urlDomain) ? urlDomain : list[0];
          });
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load skill stats when domain changes
  const loadStats = useCallback(() => {
    if (!selectedDomain) return;
    setStatsLoading(true);
    adaptiveAPI.getItemBankStats(selectedDomain)
      .then(({ data }) => {
        // API returns per_skill; also accept skills/stats for backwards compat
        const list = data?.per_skill || data?.skills || data?.stats || [];
        setSkills(list);
        if (list.length) setActiveSkillId((prev) => prev || list[0]?.skill_id || list[0]?.id);
      })
      .catch(() => setSkills([]))
      .finally(() => setStatsLoading(false));
  }, [selectedDomain]);

  useEffect(() => {
    setActiveSkillId(null);
    setItems([]);
    loadStats();
  }, [loadStats]);

  // Load items when skill or page changes
  const loadItems = useCallback(() => {
    if (!selectedDomain || !activeSkillId) return;
    setItemsLoading(true);
    adaptiveAPI.getItemBank(selectedDomain, { skill_id: activeSkillId, page, per_page: PER_PAGE })
      .then(({ data }) => {
        setItems(data?.items || data?.results || []);
        const total = data?.total || 0;
        setTotalPages(Math.max(1, Math.ceil(total / PER_PAGE)));
      })
      .catch(() => setItems([]))
      .finally(() => setItemsLoading(false));
  }, [selectedDomain, activeSkillId, page]);

  useEffect(() => {
    setPage(1);
  }, [activeSkillId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleDelete = async (itemId) => {
    if (!window.confirm('Soft-delete this item? It will be hidden from learners.')) return;
    try {
      await adaptiveAPI.deleteItemBankEntry(selectedDomain, itemId);
      loadItems();
    } catch {
      alert('Delete failed.');
    }
  };

  const handleEditSave = () => {
    setExpandedItemId(null);
    loadItems();
  };

  const activeSkill = skills.find((s) => (s.skill_id || s.id) === activeSkillId);

  return (
    <CoachLayout title="Item Bank Manager">
      {showGenerate && (
        <GenerateModal
          domain={selectedDomain}
          skillId={activeSkillId}
          onClose={() => setShowGenerate(false)}
        />
      )}

      {/* ── Domain selector ────────────────────────────────────────────────── */}
      <div className="coach-section">
        <div className="coach-section-header">
          <h2 className="coach-section-title">Item Bank</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              className="coach-select"
              style={{ width: 180 }}
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              data-testid="item-bank-domain-selector"
            >
              {domains.map((d) => (
                <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1).replace(/_/g, ' ')}</option>
              ))}
            </select>
            <button
              className="btn btn-primary"
              data-testid="generate-items-btn"
              onClick={() => setShowGenerate(true)}
              disabled={!activeSkillId}
            >
              + Generate More
            </button>
          </div>
        </div>

        {/* ── Skill tabs ─────────────────────────────────────────────────────── */}
        {statsLoading ? (
          <div className="coach-loading">Loading skills…</div>
        ) : skills.length === 0 ? (
          <div className="coach-empty">
            <span className="coach-empty-icon">📋</span>
            <div className="coach-empty-title">No skills found</div>
            <div className="coach-empty-desc">No item bank stats available for this domain yet.</div>
          </div>
        ) : (
          <>
            {/* Skill tab bar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
              {skills.map((s) => {
                const skillId = s.skill_id || s.id;
                const isActive = skillId === activeSkillId;
                const cnt = s.item_count ?? s.count ?? 0;
                const barColor = coverageColor(cnt);
                return (
                  <button
                    key={skillId}
                    data-testid={`item-bank-skill-tab-${skillId}`}
                    onClick={() => setActiveSkillId(skillId)}
                    style={{
                      padding: '5px 14px',
                      borderRadius: 9999,
                      border: `2px solid ${isActive ? '#6366f1' : 'rgba(255,255,255,0.12)'}`,
                      background: isActive ? 'rgba(99,102,241,0.18)' : 'rgba(255,255,255,0.04)',
                      color: isActive ? '#a78bfa' : '#94a3b8',
                      cursor: 'pointer',
                      fontSize: '0.82rem',
                      fontWeight: isActive ? 700 : 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                    }}
                  >
                    {s.label || s.display_name || skillId.split('.').pop()}
                    <span style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: barColor,
                      flexShrink: 0,
                    }} />
                  </button>
                );
              })}
            </div>

            {/* Coverage bar for active skill */}
            {activeSkill && (() => {
              const cnt = activeSkill.item_count ?? activeSkill.count ?? 0;
              const barColor = coverageColor(cnt);
              const pct = Math.min(100, Math.round((cnt / 25) * 100));
              return (
                <div style={{ marginBottom: 14 }} data-testid={`coverage-bar-${activeSkillId}`}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                      Item coverage — {cnt} item{cnt !== 1 ? 's' : ''}
                    </span>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: barColor }}>
                      {cnt >= 25 ? '✓ Good' : cnt >= 10 ? '⚠ Moderate' : '✗ Low'}
                    </span>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 3, transition: 'width 0.3s' }} />
                  </div>
                </div>
              );
            })()}

            {/* Items table */}
            {itemsLoading ? (
              <div className="coach-loading">Loading items…</div>
            ) : items.length === 0 ? (
              <div className="coach-empty" style={{ padding: '20px 0' }}>
                <span className="coach-empty-icon">🗒️</span>
                <div className="coach-empty-title">No items yet</div>
                <div className="coach-empty-desc">
                  Use <strong>+ Generate More</strong> to create items with LLM, or add them manually.
                </div>
              </div>
            ) : (
              <>
                <table className="coach-table" data-testid="item-bank-table">
                  <thead>
                    <tr>
                      <th>Question Preview</th>
                      <th style={{ width: 110 }}>Difficulty</th>
                      <th style={{ width: 90 }}>IRT b</th>
                      <th style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const itemId = item.item_id || item._id;
                      const isExpanded = expandedItemId === itemId;
                      const preview = (item.question_text || item.stem || '').slice(0, 80);
                      const diffLabel = item.difficulty_label || item.difficulty || '—';
                      const bParam = item.b_param != null ? item.b_param.toFixed(2) : '—';
                      return (
                        <React.Fragment key={itemId}>
                          <tr
                            style={{
                              cursor: 'pointer',
                              background: isExpanded ? 'rgba(99,102,241,0.06)' : undefined,
                            }}
                            onClick={() => setExpandedItemId(isExpanded ? null : itemId)}
                          >
                            <td style={{ color: '#cbd5e1', maxWidth: 0 }}>
                              <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {preview || <em style={{ color: '#475569' }}>No text</em>}
                              </span>
                            </td>
                            <td>
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: 999,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                background: diffLabel === 'easy' ? 'rgba(34,197,94,0.12)' : diffLabel === 'hard' ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
                                color: diffLabel === 'easy' ? '#4ade80' : diffLabel === 'hard' ? '#f87171' : '#fbbf24',
                              }}>
                                {diffLabel}
                              </span>
                            </td>
                            <td style={{ color: '#94a3b8', fontSize: '0.82rem' }}>{bParam}</td>
                            <td>
                              <button
                                className="btn btn-outline btn-sm"
                                style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)', fontSize: '0.75rem', padding: '2px 8px' }}
                                onClick={(e) => { e.stopPropagation(); handleDelete(itemId); }}
                                title="Soft-delete"
                              >
                                🗑
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <InlineEditForm
                              domain={selectedDomain}
                              item={item}
                              onSave={handleEditSave}
                              onCancel={() => setExpandedItemId(null)}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12, alignItems: 'center' }}>
                    <button className="btn btn-outline btn-sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
                    <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>Page {page} / {totalPages}</span>
                    <button className="btn btn-outline btn-sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </CoachLayout>
  );
}
